/**
 * PotatoFrameCap — holds the frame rate at POTATO.fpsCap while potato mode is
 * on, and reports the rate it ACTUALLY renders at so the adaptive budget can
 * tune resolution against it. Mount it INSIDE a <Canvas>, and only in potato
 * mode.
 *
 * How the cap works: R3F stops auto-rendering as soon as any useFrame
 * subscriber has priority > 0 — rendering becomes the subscriber's job. So this
 * component takes over the render pass and simply performs it 30 times a second
 * instead of 60+. Every other useFrame callback (priority 0) keeps running at
 * full rate, which is exactly what we want: scroll position, note-crossing
 * detection and the audio scheduler stay as accurate as ever; only the GPU does
 * less work. A stable 30 also reads better than the 27-45 jitter weak machines
 * were actually living in (telemetry: 27.6 avg / 15 p5).
 *
 * Why it reports its rate: the governor samples the rAF delta, which keeps
 * ticking on the frames this component SKIPS — so it would read ~60fps while
 * the user sees 30, and happily sharpen the picture until everything melted.
 * The count of real renders per second is the honest signal, and it degrades
 * correctly: when the machine can't keep up, rAF itself slows, the accumulator
 * overshoots, and the rendered rate falls below the cap. That's what lets
 * potato mode run the resolution ladder as a dynamic-resolution controller —
 * as sharp as this machine can hold 30fps at, no sharper.
 *
 * Because it must be MOUNTED conditionally (not registered conditionally —
 * that would break auto-render for every other mode), the parent renders it as
 * `{potato && <PotatoFrameCap />}`.
 */

import { useEffect, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { setFpsCap, reportRenderedFps } from './adaptiveBudget';

/** `cap` is required and comes from the active preset's user-editable fpsCap
 *  (performanceSettings) — mount only while that cap is > 0. */
export default function PotatoFrameCap({ cap }: { cap: number }) {
  const acc = useRef(0);
  // Rendered-rate meter: renders counted since the last report, and when that
  // window opened.
  const rendered = useRef(0);
  const windowStart = useRef(0);

  // Announce the cap for as long as this component is mounted, so the tuner's
  // "is this framerate good?" thresholds move to sit just under it.
  useEffect(() => {
    setFpsCap(cap);
    return () => { setFpsCap(null); reportRenderedFps(null); };
  }, [cap]);

  useFrame(({ gl, scene, camera }, delta) => {
    // Ignore absurd deltas (tab was hidden) so we don't burst-render on return.
    acc.current += Math.min(delta, 0.25);
    const period = 1 / Math.max(1, cap);
    if (acc.current < period) return;      // skip the render, keep the logic
    acc.current %= period;                 // remainder, not reset → no drift
    gl.render(scene, camera);

    // Report the achieved rate about twice a second — long enough to be a rate
    // rather than a sample, short enough that the ladder reacts in time.
    const now = performance.now();
    if (windowStart.current === 0) { windowStart.current = now; rendered.current = 0; return; }
    rendered.current++;
    const elapsed = now - windowStart.current;
    if (elapsed >= 500) {
      reportRenderedFps((rendered.current * 1000) / elapsed);
      windowStart.current = now;
      rendered.current = 0;
    }
  }, 1);                                   // priority > 0 ⇒ we own the render

  return null;
}
